import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, fmtDate, downloadFile } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Stethoscope, Plus, Pencil, Trash2, X, Download, Users, Building2, AlertTriangle, CalendarClock, ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import { VisitaForm, emptyVisita, TIPO_VISITA, GIUDIZIO } from "../components/VisitaForm";

const ALL = "__all__";
const label = (arr, v) => (arr.find((x) => x.v === v) || {}).l || v || "—";

function scadBadge(d) {
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(d + "T00:00:00") - today) / 86400000);
  if (days < 0) return { t: `Scaduta ${fmtDate(d)}`, c: "bg-rose-100 text-rose-700 border-rose-200" };
  if (days <= 30) return { t: `Scade ${fmtDate(d)}`, c: "bg-amber-100 text-amber-700 border-amber-200" };
  return { t: `Prossima ${fmtDate(d)}`, c: "bg-emerald-50 text-emerald-700 border-emerald-200" };
}

const TONES = {
  teal: ["border-teal-200 from-teal-50", "text-teal-700", "text-teal-800"],
  slate: ["border-slate-200 from-slate-50", "text-slate-600", "text-slate-800"],
  amber: ["border-amber-200 from-amber-50", "text-amber-700", "text-amber-800"],
  rose: ["border-rose-200 from-rose-50", "text-rose-700", "text-rose-800"],
};
function Kpi({ testid, icon: Icon, label: l, value, tone }) {
  const [card, lab, val] = TONES[tone];
  return (
    <Card className={`p-4 bg-gradient-to-br to-white ${card}`} data-testid={testid}>
      <div className={`text-xs flex items-center gap-1.5 ${lab}`}><Icon size={14} /> {l}</div>
      <div className={`mt-1 font-display text-xl font-bold ${val}`}>{value || 0}</div>
    </Card>
  );
}

function VisitaRow({ v, onEdit, onDelete }) {
  const b = scadBadge(v.scadenza_prossima);
  return (
    <tr className="hover:bg-slate-50" data-testid={`visita-row-${v.id}`}>
      <td className="px-4 py-2"><div className="font-medium text-slate-800">{v.lavoratore_nome}</div><div className="text-xs text-slate-400">{v.codice_fiscale}</div></td>
      <td className="px-4 py-2 text-slate-600">{v.mansione || "—"}</td>
      <td className="px-4 py-2 text-slate-600">{label(TIPO_VISITA, v.tipo_visita)}<div className="text-xs text-slate-400">{v.data_visita ? fmtDate(v.data_visita) : "—"}</div></td>
      <td className="px-4 py-2 text-slate-600 text-xs">{label(GIUDIZIO, v.giudizio_idoneita)}</td>
      <td className="px-4 py-2">{b && <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${b.c}`}>{b.t}</span>}</td>
      <td className="px-4 py-2"><div className="flex justify-end gap-1.5">
        <Button size="sm" variant="outline" className="h-8" onClick={() => onEdit(v)} data-testid={`edit-visita-${v.id}`}><Pencil size={14} /></Button>
        <Button size="sm" className="h-8 bg-rose-600 hover:bg-rose-700 text-white" onClick={() => onDelete(v)} data-testid={`delete-visita-${v.id}`}><Trash2 size={14} /></Button>
      </div></td>
    </tr>
  );
}

function AziendaGroup({ medico, azienda, visite, anno, onEdit, onDelete }) {
  const pdf = async () => {
    const qs = new URLSearchParams({ azienda });
    if (anno !== ALL) qs.set("anno", anno);
    if (medico !== "—") qs.set("medico", medico);
    try { await downloadFile(`/medico-competente/pdf?${qs}`, `Allegato3B_${azienda.replace(/\s+/g, "_")}${anno !== ALL ? "_" + anno : ""}.pdf`); }
    catch { toast.error("Errore PDF"); }
  };
  return (
    <div className="border-t border-slate-100" data-testid={`group-azienda-${medico}-${azienda}`}>
      <div className="flex items-center justify-between px-4 py-2 bg-slate-50/60">
        <div className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><Building2 size={14} className="text-teal-600" /> {azienda} <span className="text-xs text-slate-400">· {visite.length} visite</span></div>
        <Button size="sm" variant="outline" className="h-7 border-teal-300 text-teal-700" onClick={pdf} data-testid={`pdf-${medico}-${azienda}`}><Download size={13} className="mr-1" /> PDF Allegato 3B</Button>
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm table-fixed min-w-[760px]">
        <colgroup><col className="w-[22%]" /><col className="w-[16%]" /><col className="w-[16%]" /><col className="w-[20%]" /><col className="w-[14%]" /><col className="w-[12%]" /></colgroup>
        <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-100">
          <th className="px-4 py-2 font-medium">Lavoratore</th><th className="px-4 py-2 font-medium">Mansione</th><th className="px-4 py-2 font-medium">Tipo / Data</th>
          <th className="px-4 py-2 font-medium">Giudizio</th><th className="px-4 py-2 font-medium">Scadenza</th><th className="px-4 py-2 font-medium text-right">Azioni</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-100">
        {visite.map((v) => <VisitaRow key={v.id} v={v} onEdit={onEdit} onDelete={onDelete} />)}
      </tbody></table></div>
    </div>
  );
}

function MedicoGroup({ medico, aziende, anno, onEdit, onDelete }) {
  const [open, setOpen] = useState(true);
  const n = Object.values(aziende).reduce((s, a) => s + a.length, 0);
  return (
    <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid={`group-medico-${medico}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid={`toggle-medico-${medico}`}>
        <div className="flex items-center gap-2 font-display font-semibold text-slate-800">{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}<Stethoscope size={16} className="text-teal-600" /> {medico}</div>
        <span className="text-xs text-slate-500">{Object.keys(aziende).length} aziende · {n} visite</span>
      </button>
      {open && Object.entries(aziende).sort().map(([az, vs]) => (
        <AziendaGroup key={az} medico={medico} azienda={az} visite={vs} anno={anno} onEdit={onEdit} onDelete={onDelete} />
      ))}
    </Card>
  );
}

export default function MedicoCompetente() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [incarichi, setIncarichi] = useState([]);
  const [anno, setAnno] = useState(String(new Date().getFullYear()));
  const [medico, setMedico] = useState(ALL);
  const [azienda, setAzienda] = useState(ALL);
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    const qs = new URLSearchParams();
    if (anno !== ALL) qs.set("anno", anno);
    if (medico !== ALL) qs.set("medico", medico);
    if (azienda !== ALL) qs.set("azienda", azienda);
    api.get(`/medico-competente/visite?${qs}`).then((r) => setItems(r.data)).catch(() => {});
    api.get(`/medico-competente/summary${anno !== ALL ? `?anno=${anno}` : ""}`).then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(load, [anno, medico, azienda]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/medico-competente/incarichi").then((r) => setIncarichi(r.data)).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const g = {};
    items.filter((v) => !q || `${v.lavoratore_nome} ${v.codice_fiscale} ${v.azienda} ${v.medico_competente} ${v.mansione}`.toLowerCase().includes(q))
      .forEach((v) => {
        const m = v.medico_competente || "—"; const a = v.azienda || "—";
        ((g[m] = g[m] || {})[a] = g[m][a] || []).push(v);
      });
    return g;
  }, [items, search]);

  const years = Array.from({ length: 6 }, (_, i) => String(new Date().getFullYear() + 1 - i));
  const setV = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, ...patch } }));

  const save = async () => {
    const f = dialog.form;
    if (!f.lavoratore_nome.trim() || !f.azienda.trim() || !f.medico_competente.trim()) return toast.error("Lavoratore, azienda e medico sono obbligatori");
    setSaving(true);
    try {
      if (dialog.id) await api.put(`/medico-competente/visite/${dialog.id}`, f);
      else await api.post("/medico-competente/visite", f);
      toast.success(dialog.id ? "Visita aggiornata" : "Visita registrata");
      setDialog(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const remove = async (v) => {
    if (!window.confirm(`Eliminare la visita di ${v.lavoratore_nome}?`)) return;
    try { await api.delete(`/medico-competente/visite/${v.id}`); toast.success("Visita eliminata"); load(); } catch { toast.error("Errore"); }
  };
  const edit = (v) => setDialog({ id: v.id, form: { ...emptyVisita(), ...v } });

  return (
    <div className="space-y-6 animate-fade-up" data-testid="medico-competente-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
            <span className="inline-flex w-9 h-9 items-center justify-center rounded-xl bg-teal-600 text-white shadow-sm"><Stethoscope size={18} /></span> Medico Competente
          </h1>
          <p className="text-sm text-slate-500 mt-1">Registro delle visite mediche effettuate dai medici incaricati, per azienda, con PDF Allegato 3B.</p>
        </div>
        <Button onClick={() => setDialog({ id: null, form: emptyVisita() })} className="bg-teal-600 hover:bg-teal-700 text-white shadow-sm" data-testid="new-visita-btn"><Plus size={16} className="mr-1.5" /> Nuova visita</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="kpi-visite" icon={CalendarClock} label={`Visite ${anno !== ALL ? anno : "totali"}`} value={summary?.visite} tone="teal" />
        <Kpi testid="kpi-medici" icon={Users} label="Medici" value={summary?.medici} tone="slate" />
        <Kpi testid="kpi-in-scadenza" icon={CalendarClock} label="In scadenza (30gg)" value={summary?.in_scadenza} tone="amber" />
        <Kpi testid="kpi-scadute" icon={AlertTriangle} label="Visite scadute" value={summary?.scadute} tone="rose" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={anno} onValueChange={setAnno}><SelectTrigger className="w-32" data-testid="filter-anno"><SelectValue /></SelectTrigger><SelectContent><SelectItem value={ALL}>Tutti gli anni</SelectItem>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent></Select>
        <Select value={medico} onValueChange={setMedico}><SelectTrigger className="w-52" data-testid="filter-medico"><SelectValue /></SelectTrigger><SelectContent><SelectItem value={ALL}>Tutti i medici</SelectItem>{(summary?.elenco_medici || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
        <Select value={azienda} onValueChange={setAzienda}><SelectTrigger className="w-56" data-testid="filter-azienda"><SelectValue /></SelectTrigger><SelectContent><SelectItem value={ALL}>Tutte le aziende</SelectItem>{(summary?.elenco_aziende || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
        <div className="relative w-full sm:w-72 sm:ml-auto">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca lavoratore, CF, mansione…" className="pr-8" data-testid="mc-search" />
          {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" data-testid="mc-search-clear"><X size={15} /></button>}
        </div>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <Card className="py-14 text-center text-slate-400" data-testid="mc-empty">Nessuna visita registrata per i filtri selezionati.</Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).sort().map(([m, az]) => <MedicoGroup key={m} medico={m} aziende={az} anno={anno} onEdit={edit} onDelete={remove} />)}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="visita-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{dialog?.id ? "Modifica visita medica" : "Nuova visita medica"}</DialogTitle>
            <DialogDescription>Azienda, medico incaricato, lavoratore ed esito della visita (dati Allegato 3B).</DialogDescription>
          </DialogHeader>
          {dialog && <VisitaForm v={dialog.form} set={setV} companies={companies} incarichi={incarichi} medici={summary?.elenco_medici || []} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} data-testid="visita-cancel-btn">Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="visita-save-btn"><CheckCircle2 size={16} className="mr-1.5" /> {saving ? "Salvataggio…" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
